"use client"

import { useState, useEffect } from "react"
import { AuthLayout } from "@/components/auth/auth-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useRouter } from "@/src/hooks/useRouter"
import { useAuth } from "@/src/contexts/AuthContext"
import { useKyc } from "@/src/contexts/KycContext"
import { User, Building2, CheckCircle2, FileText } from "lucide-react"
import { verifyIndividual, type IndividualKycData } from "@/lib/api/registration"
import DocumentUploader from "@/components/kyc/DocumentUploader"
import DocumentList from "@/components/kyc/DocumentList"
import type { KycDocument } from "@/lib/api/kyc-documents"
import { getCustomerDocuments } from "@/lib/api/kyc-documents"
import { useThaiLocationForm } from "@/hooks/useThaiLocationForm"
import { ThaiLocationSelect } from "@/components/forms/ThaiLocationSelect"

export default function VerifyIndividualPage() {
  const router = useRouter()
  const { refreshUser } = useAuth()
  const { kycStatus } = useKyc()
  
  // Location form state (Thai provinces, amphures, districts)
  const {
    formData: locationData,
    loading: locationLoading,
    provinces,
    amphures,
    districts,
    handleProvinceChange,
    handleAmphureChange,
    handleDistrictChange,
    setData: setLocationData
  } = useThaiLocationForm({
    onError: (error) => {
      console.error('Location API error:', error)
      setError('ไม่สามารถโหลดข้อมูลที่อยู่ได้ กรุณาลองใหม่อีกครั้ง')
    }
  })

  // Tax invoice delivery location form state (separate cascading dropdowns)
  const {
    formData: taxLocationData,
    loading: taxLocationLoading,
    provinces: taxProvinces,
    amphures: taxAmphures,
    districts: taxDistricts,
    handleProvinceChange: handleTaxProvinceChange,
    handleAmphureChange: handleTaxAmphureChange,
    handleDistrictChange: handleTaxDistrictChange,
    setData: setTaxLocationData,
    reset: resetTaxLocation,
  } = useThaiLocationForm({
    onError: (error) => {
      console.error('Tax delivery location API error:', error)
      setError('ไม่สามารถโหลดข้อมูลที่อยู่ได้ กรุณาลองใหม่อีกครั้ง')
    }
  })
  
  // Personal info form state
  const [formData, setFormData] = useState({
    idCard: "",
    prefix: "",
    firstName: "",
    lastName: "",
    address: "",
  })
  const [useAddressForTax, setUseAddressForTax] = useState<boolean | null>(null)
  const [taxInvoiceDeliveryData, setTaxInvoiceDeliveryData] = useState({
    address: "",
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    idCardFront?: KycDocument;
    idCardBack?: KycDocument;
    houseRegistration?: KycDocument;
  }>({})
  const [refreshDocuments, setRefreshDocuments] = useState(0)

  useEffect(() => {
    const token = localStorage.getItem("accessToken") || localStorage.getItem("authToken")
    if (!token) {
      router.push("/auth/login")
    }
  }, [router])

  // Load existing documents when component mounts
  useEffect(() => {
    const loadExistingDocuments = async () => {
      try {
        const response = await getCustomerDocuments();
        if (response.data && response.data.length > 0) {
          const docs = response.data.reduce((acc, doc) => {
            if (doc.document_type === 'id_card_front') {
              acc.idCardFront = doc;
            } else if (doc.document_type === 'id_card_back') {
              acc.idCardBack = doc;
            } else if (doc.document_type === 'house_registration') {
              acc.houseRegistration = doc;
            }
            return acc;
          }, {} as typeof uploadedDocuments);
          
          setUploadedDocuments(docs);
        }
      } catch (err) {
        console.error('Failed to load existing documents:', err);
        // Silent fail - user can still upload new documents
      }
    }
    
    loadExistingDocuments();
  }, [])

  // Pre-fill form if KYC was rejected
  useEffect(() => {
    if (kycStatus?.status === 'rejected' && kycStatus.kycData) {
      const data = kycStatus.kycData
      
      // Pre-fill personal info
      setFormData({
        idCard: data.idCard || "",
        prefix: data.prefix || "",
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        address: data.address || "",
      })
      
      // Pre-fill location data
      if (data.province && data.subDistrict && data.district) {
        setLocationData({
          province: data.province,
          amphure: data.subDistrict,  // Backend: subDistrict → Frontend: amphure
          tambon: data.district,      // Backend: district → Frontend: tambon
          postalCode: data.postalCode || ""
        })
      }

      setTaxInvoiceDeliveryData({
        address: data.taxInvoiceDeliveryAddress || "",
      })

      if (
        data.taxInvoiceDeliveryProvince &&
        data.taxInvoiceDeliverySubDistrict &&
        data.taxInvoiceDeliveryDistrict
      ) {
        setTaxLocationData({
          province: data.taxInvoiceDeliveryProvince,
          amphure: data.taxInvoiceDeliverySubDistrict,
          tambon: data.taxInvoiceDeliveryDistrict,
          postalCode: data.taxInvoiceDeliveryPostalCode || "",
        })
      }
      
      setUseAddressForTax(data.useAddressForTax !== false)
    }
  }, [kycStatus])

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError("") // Clear error when user types
  }

  const handleAddressTypeChange = (useTaxAddress: boolean) => {
    setUseAddressForTax(useTaxAddress)
    setError("")
    if (useTaxAddress) {
      setTaxInvoiceDeliveryData({
        address: "",
      })
      resetTaxLocation()
    }
  }

  const handleTaxDeliveryInputChange = (field: "address", value: string) => {
    setTaxInvoiceDeliveryData((prev) => ({ ...prev, [field]: value }))
    setError("")
  }

  const handleBack = () => {
    router.push("/auth/verify")
  }

  const handleNext = async () => {
    setError("")
    
    // Validation
    if (!formData.idCard || formData.idCard.length !== 13) {
      setError("กรุณากรอกเลขบัตรประจำตัวประชาชน 13 หลัก")
      return
    }
    if (!formData.prefix || !formData.firstName || !formData.lastName) {
      setError("กรุณากรอกข้อมูลส่วนตัวให้ครบถ้วน")
      return
    }
    // Validate location data from Thai location dropdowns
    if (!locationData.tambon || !locationData.amphure || !locationData.province) {
      setError("กรุณาเลือกข้อมูลที่อยู่ให้ครบถ้วน")
      return
    }
    if (!locationData.postalCode || locationData.postalCode.length !== 5) {
      setError("กรุณาเลือกตำบลเพื่อให้ระบบกรอกรหัสไปรษณีย์อัตโนมัติ")
      return
    }
    if (useAddressForTax === null) {
      setError("กรุณาเลือกที่อยู่สำหรับจัดส่งใบกำกับภาษี")
      return
    }
    if (useAddressForTax === false) {
      if (!taxInvoiceDeliveryData.address.trim()) {
        setError("กรุณากรอกที่อยู่สำหรับจัดส่งใบกำกับภาษี")
        return
      }
      if (!taxLocationData.tambon.trim()) {
        setError("กรุณาเลือกแขวง/ตำบลสำหรับจัดส่งใบกำกับภาษี")
        return
      }
      if (!taxLocationData.amphure.trim()) {
        setError("กรุณาเลือกเขต/อำเภอสำหรับจัดส่งใบกำกับภาษี")
        return
      }
      if (!taxLocationData.province.trim()) {
        setError("กรุณาเลือกจังหวัดสำหรับจัดส่งใบกำกับภาษี")
        return
      }
      if (!/^\d{5}$/.test(taxLocationData.postalCode.trim())) {
        setError("กรุณาเลือกตำบลสำหรับจัดส่งใบกำกับภาษี เพื่อให้ระบบกรอกรหัสไปรษณีย์อัตโนมัติ")
        return
      }
    }
    // Validate document uploads - require ID card front and back
    if (!uploadedDocuments.idCardFront) {
      setError("กรุณาอัพโหลดบัตรประชาชนด้านหน้า")
      return
    }
    if (!uploadedDocuments.idCardBack) {
      setError("กรุณาอัพโหลดบัตรประชาชนด้านหลัง")
      return
    }

    setLoading(true)

    try {
      const token = localStorage.getItem("accessToken") || localStorage.getItem("authToken")
      if (!token) {
        setError("ไม่พบข้อมูลการเข้าสู่ระบบ")
        return
      }

      // Map locationData fields to backend expected names
      const kycData: IndividualKycData = {
        idCard: formData.idCard,
        prefix: formData.prefix,
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address || "",
        district: locationData.tambon,      // Frontend: tambon → Backend: district
        subDistrict: locationData.amphure,  // Frontend: amphure → Backend: subDistrict
        province: locationData.province,    // Same name
        postalCode: locationData.postalCode, // Same name
        useAddressForTax: useAddressForTax === true,
        taxInvoiceDeliveryAddress: useAddressForTax ? undefined : taxInvoiceDeliveryData.address.trim(),
        taxInvoiceDeliveryDistrict: useAddressForTax ? undefined : taxLocationData.tambon.trim(),
        taxInvoiceDeliverySubDistrict: useAddressForTax ? undefined : taxLocationData.amphure.trim(),
        taxInvoiceDeliveryProvince: useAddressForTax ? undefined : taxLocationData.province.trim(),
        taxInvoiceDeliveryPostalCode: useAddressForTax ? undefined : taxLocationData.postalCode.trim(),
      }

      await verifyIndividual(token, kycData)
      
      // Update localStorage with KYC status
      const user = JSON.parse(localStorage.getItem("user") || "{}")
      user.kycVerified = true
      user.kycType = "individual"
      localStorage.setItem("user", JSON.stringify(user))

      // Refresh AuthContext to sync with localStorage
      await refreshUser()

      setSuccess(true)
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        router.push("/dashboard")
      }, 2000)
    } catch (err: any) {
      setError(err.message || "เกิดข้อผิดพลาดในการยืนยันตัวตน")
    } finally {
      setLoading(false)
    }
  }

  // Success Screen
  if (success) {
    return (
      <AuthLayout>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-3xl mx-auto text-center">
          <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-[#323130] mb-4">
            ยืนยันตัวตนสำเร็จ!
          </h1>
          <p className="text-[#949494] text-lg mb-6">
            ข้อมูลของคุณได้รับการบันทึกเรียบร้อยแล้ว
          </p>
          <p className="text-[#949494]">
            กำลังนำคุณไปยังหน้าแดชบอร์ด...
          </p>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#323130] mb-2">
            กรอกข้อมูลสำหรับคำสั่งซื้อ
          </h1>
          <p className="text-[#949494] text-sm">ประเภทการจดทะเบียนบุริษัท</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Type Selection Tabs */}
        <div className="flex gap-4 mb-8">
          <button className="flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border-2 border-[#4169E1] bg-[#E8F0FE] transition-all">
            <User className="w-6 h-6 text-[#4169E1]" />
            <span className="font-semibold text-[#323130]">บุคคลธรรมดา</span>
          </button>
          <button
            onClick={() => router.push("/auth/verify/company")}
            className="flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border-2 border-[#e5e5e5] bg-white hover:border-[#d1d1d1] transition-all"
          >
            <Building2 className="w-6 h-6 text-[#949494]" />
            <span className="font-semibold text-[#949494]">นิติบุคคล</span>
          </button>
        </div>

        {/* Form Section */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-[#323130] mb-4">
            ข้อมูลสำหรับออกใบกำกับภาษี
          </h2>

          {/* ID Card Number */}
          <div className="space-y-2">
            <Label htmlFor="idCard" className="text-[#323130] font-medium">
              เลขบัตรประจำตัวประชาชน <span className="text-red-500">*</span>
            </Label>
            <Input
              id="idCard"
              type="text"
              value={formData.idCard}
              onChange={(e) => handleInputChange("idCard", e.target.value.replace(/\D/g, ""))}
              placeholder="เลขบัตรประจำตัวประชาชน 13 หลัก"
              maxLength={13}
              disabled={loading}
              className="bg-white border-[#d1d1d1] text-[#323130]"
            />
          </div>

          {/* Prefix */}
          <div className="space-y-2">
            <Label htmlFor="prefix" className="text-[#323130] font-medium">
              คำนำหน้า <span className="text-red-500">*</span>
            </Label>
            <Input
              id="prefix"
              type="text"
              value={formData.prefix}
              onChange={(e) => handleInputChange("prefix", e.target.value)}
              placeholder="คำนำหน้า เช่น นาย, นางสาว, อาจารย์, ฯลฯ"
              disabled={loading}
              className="bg-white border-[#d1d1d1] text-[#323130]"
            />
          </div>

          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-[#323130] font-medium">
              ชื่อ <span className="text-red-500">*</span>
            </Label>
            <Input
              id="firstName"
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              placeholder="ชื่อ"
              disabled={loading}
              className="bg-white border-[#d1d1d1] text-[#323130]"
            />
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-[#323130] font-medium">
              นามสกุล <span className="text-red-500">*</span>
            </Label>
            <Input
              id="lastName"
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              placeholder="นามสกุล"
              disabled={loading}
              className="bg-white border-[#d1d1d1] text-[#323130]"
            />
          </div>

          {/* Address (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="address" className="text-[#323130] font-medium">
              ที่อยู่
            </Label>
            <Input
              id="address"
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              placeholder="ที่อยู่ (ถ้ามี)"
              disabled={loading}
              className="bg-white border-[#d1d1d1] text-[#323130]"
            />
          </div>

          {/* Thai Location Selection - Cascading Dropdowns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <ThaiLocationSelect
                label="จังหวัด"
                value={locationData.province}
                onChange={handleProvinceChange}
                options={provinces}
                placeholder="เลือกจังหวัด"
                disabled={loading || locationLoading.provinces}
                loading={locationLoading.provinces}
                required
              />
            </div>
            <div className="space-y-2">
              <ThaiLocationSelect
                label="เขต / อำเภอ"
                value={locationData.amphure}
                onChange={handleAmphureChange}
                options={amphures}
                placeholder={locationData.provinceId ? "เลือกเขต / อำเภอ" : "กรุณาเลือกจังหวัดก่อน"}
                disabled={loading || !locationData.provinceId || locationLoading.amphures}
                loading={locationLoading.amphures}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <ThaiLocationSelect
                label="แขวง / ตำบล"
                value={locationData.tambon}
                onChange={handleDistrictChange}
                options={districts}
                placeholder={locationData.amphureId ? "เลือกแขวง / ตำบล" : "กรุณาเลือกเขต / อำเภอก่อน"}
                disabled={loading || !locationData.amphureId || locationLoading.districts}
                loading={locationLoading.districts}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postalCode" className="text-[#323130] font-medium">
                รหัสไปรษณีย์ <span className="text-red-500">*</span>
              </Label>
              <Input
                id="postalCode"
                type="text"
                value={locationData.postalCode}
                readOnly
                placeholder="จะกรอกอัตโนมัติเมื่อเลือกตำบล"
                disabled
                className="bg-gray-50 border-[#d1d1d1] text-[#323130]"
              />
            </div>
          </div>

          {/* Address for Tax Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-[#323130]">
              ที่อยู่สำหรับจัดส่งใบกำกับภาษี
            </h2>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="sameAddress"
                  name="addressType"
                  checked={useAddressForTax === true}
                  onChange={() => handleAddressTypeChange(true)}
                  disabled={loading}
                  className="w-5 h-5 text-[#4169E1] border-[#d1d1d1] focus:ring-[#4169E1]"
                />
                <Label htmlFor="sameAddress" className="text-[#323130] font-medium cursor-pointer">
                  ใช้ที่อยู่สำหรับออกใบกำกับภาษี
                </Label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="differentAddress"
                  name="addressType"
                  checked={useAddressForTax === false}
                  onChange={() => handleAddressTypeChange(false)}
                  disabled={loading}
                  className="w-5 h-5 text-[#4169E1] border-[#d1d1d1] focus:ring-[#4169E1]"
                />
                <Label htmlFor="differentAddress" className="text-[#323130] font-medium cursor-pointer">
                  ใช้ที่อยู่อื่น
                </Label>
              </div>
            </div>

            {useAddressForTax === false && (
              <div className="space-y-4">
                <Label htmlFor="taxInvoiceDeliveryAddress" className="text-[#323130] font-medium">
                  ที่อยู่สำหรับจัดส่งใบกำกับภาษี <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="taxInvoiceDeliveryAddress"
                  type="text"
                  value={taxInvoiceDeliveryData.address}
                  onChange={(e) => handleTaxDeliveryInputChange("address", e.target.value)}
                  placeholder="กรอกที่อยู่สำหรับจัดส่งใบกำกับภาษี"
                  disabled={loading}
                  className="bg-white border-[#d1d1d1] text-[#323130]"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <ThaiLocationSelect
                      label="จังหวัด"
                      value={taxLocationData.province}
                      onChange={(value, id) => {
                        handleTaxProvinceChange(value, id)
                        setError("")
                      }}
                      options={taxProvinces}
                      placeholder="เลือกจังหวัด"
                      disabled={loading || taxLocationLoading.provinces}
                      loading={taxLocationLoading.provinces}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="taxInvoiceDeliveryPostalCode" className="text-[#323130] font-medium">
                      รหัสไปรษณีย์ <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="taxInvoiceDeliveryPostalCode"
                      type="text"
                      value={taxLocationData.postalCode}
                      readOnly
                      placeholder="จะกรอกอัตโนมัติเมื่อเลือกตำบล"
                      disabled
                      className="bg-gray-50 border-[#d1d1d1] text-[#323130]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <ThaiLocationSelect
                      label="เขต / อำเภอ"
                      value={taxLocationData.amphure}
                      onChange={(value, id) => {
                        handleTaxAmphureChange(value, id)
                        setError("")
                      }}
                      options={taxAmphures}
                      placeholder={taxLocationData.provinceId ? "เลือกเขต / อำเภอ" : "กรุณาเลือกจังหวัดก่อน"}
                      disabled={loading || !taxLocationData.provinceId || taxLocationLoading.amphures}
                      loading={taxLocationLoading.amphures}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <ThaiLocationSelect
                      label="แขวง / ตำบล"
                      value={taxLocationData.tambon}
                      onChange={(value, id) => {
                        handleTaxDistrictChange(value, id)
                        setError("")
                      }}
                      options={taxDistricts}
                      placeholder={taxLocationData.amphureId ? "เลือกแขวง / ตำบล" : "กรุณาเลือกเขต / อำเภอก่อน"}
                      disabled={loading || !taxLocationData.amphureId || taxLocationLoading.districts}
                      loading={taxLocationLoading.districts}
                      required
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Document Upload Section */}
          <div className="space-y-6 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-[#4169E1]" />
              <h2 className="text-xl font-semibold text-[#323130]">
                อัพโหลดเอกสารยืนยันตัวตน
              </h2>
            </div>

            <div className="space-y-6">
              {/* ID Card Front */}
              <DocumentUploader
                documentType="id_card_front"
                label="บัตรประชาชนด้านหน้า"
                description="กรุณาถ่ายภาพหรืออัพโหลดสำเนาบัตรประชาชนด้านหน้าที่ชัดเจน"
                existingDocument={uploadedDocuments.idCardFront}
                onUploadSuccess={(doc) => {
                  setUploadedDocuments(prev => ({ ...prev, idCardFront: doc }));
                  setRefreshDocuments(prev => prev + 1);
                }}
                disabled={loading}
              />

              {/* ID Card Back */}
              <DocumentUploader
                documentType="id_card_back"
                label="บัตรประชาชนด้านหลัง"
                description="กรุณาถ่ายภาพหรืออัพโหลดสำเนาบัตรประชาชนด้านหลังที่ชัดเจน"
                existingDocument={uploadedDocuments.idCardBack}
                onUploadSuccess={(doc) => {
                  setUploadedDocuments(prev => ({ ...prev, idCardBack: doc }));
                  setRefreshDocuments(prev => prev + 1);
                }}
                disabled={loading}
              />

              {/* House Registration */}
              <DocumentUploader
                documentType="house_registration"
                label="ทะเบียนบ้าน (ถ้ามี)"
                description="สำหรับยืนยันที่อยู่ปัจจุบัน"
                isRequired={false}  // Optional field - no asterisk
                existingDocument={uploadedDocuments.houseRegistration}
                onUploadSuccess={(doc) => {
                  setUploadedDocuments(prev => ({ ...prev, houseRegistration: doc }));
                  setRefreshDocuments(prev => prev + 1);
                }}
                disabled={loading}
              />
            </div>

            {/* Show uploaded documents list */}
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">เอกสารที่อัพโหลดแล้ว</h3>
              <DocumentList 
                key={refreshDocuments}
                onRefresh={() => setRefreshDocuments(prev => prev + 1)}
                onDocumentDeleted={(documentType) => {
                  // Clear the corresponding uploaded document state
                  setUploadedDocuments(prev => {
                    const newState = { ...prev };
                    if (documentType === 'id_card_front') newState.idCardFront = undefined;
                    if (documentType === 'id_card_back') newState.idCardBack = undefined;
                    if (documentType === 'house_registration') newState.houseRegistration = undefined;
                    return newState;
                  });
                  setRefreshDocuments(prev => prev + 1);
                }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6">
            <Button
              onClick={handleBack}
              variant="outline"
              disabled={loading}
              className="flex-1 border-[#d1d1d1] text-[#323130] hover:bg-[#f5f5f5] py-6 text-lg"
            >
              ยกเลิก
            </Button>
            <Button
              onClick={handleNext}
              disabled={loading}
              className="flex-1 bg-[#4169E1] hover:bg-[#3155c8] text-white py-6 text-lg"
            >
              {loading ? "กำลังบันทึก..." : "บันทึกและไปต่อ"}
            </Button>
          </div>
        </div>
      </div>
    </AuthLayout>
  )
}
